import cx from 'classnames';
import {useCallback, useEffect, useState} from 'react';
import {useGetCategories} from '~/common/components/MegaNav/MegaNavVariant/hooks/useGetCategories';
import type {
  InvitationCategoryType,
  TabsEnum,
} from '~/common/components/MegaNav/MegaNavVariant/types';
import {Divider} from '~library/atoms/Divider';
import {DrawerHeader} from './components/DrawerHeader/DrawerHeader';
import {MainLevelView} from './components/MainLevelView/MainLevelView';
import {SubcategoryLevelView} from './components/SubcategoryLevelView/SubcategoryLevelView';
import * as styles from './HamburgerMenuDrawer.module.scss';

type HamburgerMenuDrawerProps = {
  open: boolean;
  closeNav: () => void;
};

type NavigationLevel = 'main' | 'subcategories';
export type AnimationDirection = 'slide-left' | 'slide-right' | 'none';

export const HamburgerMenuDrawer = ({open, closeNav}: HamburgerMenuDrawerProps) => {
  const {getCategoriesForTab, currentGlobalNav} = useGetCategories();
  const [currentLevel, setCurrentLevel] = useState<NavigationLevel>('main');
  const [selectedCategory, setSelectedCategory] = useState<InvitationCategoryType | null>(null);
  const [activeTab, setActiveTab] = useState<TabsEnum>(currentGlobalNav);
  const [animationDirection, setAnimationDirection] = useState<AnimationDirection>('none');

  useEffect(() => {
    document.body.classList.toggle('no-scroll', open);

    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [open]);

  // Sync activeTab with currentGlobalNav when drawer opens
  useEffect(() => {
    if (open) {
      setActiveTab(currentGlobalNav);
    }
  }, [open, currentGlobalNav]);

  const handleCategoryClick = useCallback((category: InvitationCategoryType) => {
    if (category.subCategories.length === 0) {
      // If no subcategories, navigate directly to the category URL
      window.location.href = category.href;
      return;
    }

    setAnimationDirection('slide-left');
    setSelectedCategory(category);
    setCurrentLevel('subcategories');
  }, []);

  const handleBackClick = useCallback(() => {
    setAnimationDirection('slide-right');
    setCurrentLevel('main');
    setSelectedCategory(null);
  }, []);

  const handleTabClick = useCallback((tab: TabsEnum) => {
    setActiveTab(tab);
  }, []);

  const handleAnimationEnd = useCallback(() => {
    setAnimationDirection('none');
  }, []);

  // Get categories for the currently active tab
  const currentCategories = getCategoriesForTab(activeTab);

  const onCloseHandler = useCallback(() => {
    setCurrentLevel('main');
    closeNav();
  }, [closeNav]);

  return (
    <div className={cx(styles.drawer, {[styles.hidden]: !open})}>
      <div className={styles.content}>
        <DrawerHeader
          onClose={onCloseHandler}
          showBackButton={currentLevel === 'subcategories'}
          onBackClick={currentLevel === 'subcategories' ? handleBackClick : undefined}
          title={currentLevel === 'subcategories' ? selectedCategory?.title : undefined}
        />

        {currentLevel !== 'main' && <Divider className={styles.divider} />}

        <div className={styles.contentArea}>
          {currentLevel === 'main' ? (
            <MainLevelView
              categories={currentCategories}
              activeTab={activeTab}
              onCategoryClick={handleCategoryClick}
              onTabClick={handleTabClick}
              animationDirection={animationDirection}
              onAnimationEnd={handleAnimationEnd}
            />
          ) : (
            <SubcategoryLevelView
              category={selectedCategory}
              animationDirection={animationDirection}
              onAnimationEnd={handleAnimationEnd}
              closeNav={onCloseHandler}
            />
          )}
        </div>
      </div>
    </div>
  );
};
